import { Button, Drawer, Table, Typography } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useStateGlobalContext } from "../../../../../../../../contexts/GlobalContext";
import "./serviceInvoicepad.css";
import Notification from "../../../../../../../../components/Notification/Notification";
import { NumberFormatter, currencyFormatter } from "../../../../../../../../utils/validator";
import {
  fullAddress,
  fullName,
} from "../../../../../../../../components/GlobalComponents/GlobalComponents";
import FormatDateTime from "../../../../../../../../components/FormatDate/FormatDateTime";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const sectionTitleStyle = {
  display: "block",
  color: "#9ca3af",
  textTransform: "uppercase",
  fontSize: "9px",
  fontWeight: "bold",
  letterSpacing: "0.1em",
  marginBottom: "12px",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "4px",
};

const cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "20px",
  backgroundColor: "#f9fafb",
};

const labelStyle = {
  fontWeight: "bold",
  color: "#6b7280",
  width: "110px",
  flexShrink: 0,
  textTransform: "uppercase",
  fontSize: "8px",
};

const valueStyle = { flex: 1, color: "#374151" };

const FieldRow = ({ label, children }) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "6px" }}>
    <div style={labelStyle}>{label}:</div>
    <div style={valueStyle}>{children || "N/A"}</div>
  </div>
);

const column = [
  {
    key: 1,
    title: "#",
    render: (_, record, index) => index + 1,
  },
  {
    key: 2,
    title: "Service Name",
    dataIndex: "service_name",
  },
  {
    key: 3,
    title: "QTY",
    dataIndex: "quantity",
  },
  {
    key: 4,
    title: "Unit Price",
    dataIndex: "price",
    align: "center",
    render: (text) => <NumberFormatter value={text} />,
  },
  {
    key: 5,
    title: "Total Price",
    dataIndex: "price",
    align: "center",
    render: (text, record) => <NumberFormatter value={text * record?.quantity} />,
  },
];

const ServiceInvoicePad = ({ visible, onClose, rxData }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const {
    officeSetting,
    patientBasicInfo,
    getPatientData,
  } = useStateGlobalContext();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([getPatientData(id)]);
      } catch (error) {
        Notification.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const getVal = (field) => (Array.isArray(field) ? field[0] : field);
  const isVisible = (field) => (Array.isArray(field) ? field[1] === true : true);
  const fmt = (val) => currencyFormatter(val);

  const patientName = fullName(patientBasicInfo) || rxData?.patient_name;
  const patientAddr = fullAddress(patientBasicInfo);
  const issueDate = rxData?.issue_date || rxData?.date_added;
  const subTotal = rxData?.sub_total === 0 ? rxData?.price : rxData?.sub_total;
  const statusColor =
    rxData?.status === "Paid"
      ? "#16a34a"
      : rxData?.status === "Cancelled"
      ? "#dc2626"
      : "#d97706";

  const handlePrint = () => {
    const clinicName = officeSetting?.facility_name?.[0] || "Medical Facility";
    const clinicAddress =
      [
        getVal(officeSetting?.facility_address),
        getVal(officeSetting?.city),
        getVal(officeSetting?.state),
        getVal(officeSetting?.zipcode),
      ]
        .filter(Boolean)
        .join(", ") || "N/A";

    const logoUrl = officeSetting?.user_office_logo || "";
    const pName =
      [patientBasicInfo?.first_name, patientBasicInfo?.middle_name, patientBasicInfo?.last_name]
        .filter(Boolean)
        .join(" ") ||
      rxData?.patient_name ||
      "N/A";
    const pAddress =
      [
        patientBasicInfo?.current_address_line_1,
        patientBasicInfo?.current_address_line_2,
        patientBasicInfo?.current_city,
        patientBasicInfo?.current_state,
        patientBasicInfo?.current_country,
        patientBasicInfo?.current_zipcode,
      ]
        .filter(Boolean)
        .join(", ") || "N/A";
    const pEmail = patientBasicInfo?.patient_email || "N/A";
    const pPhone = patientBasicInfo?.patient_phone || "N/A";

    const dateStr = issueDate ? dayjs(issueDate).format("MM/DD/YYYY") : "N/A";
    const timeStr = issueDate ? dayjs(issueDate).format("hh:mm A") : "N/A";
    const totalPrice = (rxData?.price || 0) * (rxData?.quantity || 1);

    const billingDataHtml = [
      officeSetting?.additional_basic_data_1,
      officeSetting?.additional_basic_data_2,
      officeSetting?.additional_basic_data_3,
    ]
      .map((field) => {
        if (!isVisible(field)) return "";
        return `<div style="display:flex;margin-bottom:4px;"><span style="font-weight:bold;width:70px;flex-shrink:0;font-size:9px;text-transform:uppercase;color:#6b7280;">${field?.title || "Data"}:</span><span style="flex:1;font-size:11px;color:#374151;">${getVal(field)}</span></div>`;
      })
      .join("");

    const printHtml = `
      <html>
        <head>
          <title>Invoice - ${rxData?.invoice_id || "N/A"}</title>
          <style>
            body { font-family: 'Times New Roman', serif; padding: 30px; color: black; background: white; line-height: 1.4; margin: 0; }
            .invoice-bar { display: flex; align-items: center; justify-content: space-between; background: #e2e8f0; padding: 10px 16px; border-radius: 8px; margin-bottom: 24px; }
            .invoice-bar img { width: 48px; height: 48px; object-fit: contain; }
            .header { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 24px; align-items: stretch; }
            .box { border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #f9fafb; flex: 1; }
            .box-title { color: #9ca3af; font-size: 9px; text-transform: uppercase; font-weight: bold; letter-spacing: 0.1em; margin-bottom: 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; display: block; }
            .field { display: flex; align-items: flex-start; margin-bottom: 5px; }
            .lbl { font-weight: bold; color: #6b7280; width: 90px; flex-shrink: 0; text-transform: uppercase; font-size: 8px; }
            .val { flex: 1; font-size: 11px; color: #374151; font-family: sans-serif; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-family: sans-serif; font-size: 12px; }
            thead tr { background: #f1f5f9; }
            th { padding: 8px 12px; text-align: left; font-size: 10px; text-transform: uppercase; color: #6b7280; font-weight: bold; border-bottom: 2px solid #e2e8f0; }
            th.right, td.right { text-align: right; }
            td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #374151; }
            tbody tr:last-child td { border-bottom: none; }
            .totals { border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #f9fafb; max-width: 320px; margin-left: auto; }
            .totals-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #e5e7eb; font-family: sans-serif; font-size: 12px; }
            .totals-row:last-child { border-bottom: none; font-weight: bold; font-size: 13px; }
            @media print { body { padding: 0; } @page { margin: 12mm; } }
          </style>
        </head>
        <body>
          <div class="invoice-bar">
            ${logoUrl ? `<img src="${logoUrl}" alt="Logo" />` : `<div style="width:48px;"></div>`}
            <div style="font-weight:bold;font-size:14px;color:#475569;">
              Invoice ID: ${rxData?.invoice_id || "N/A"}
              &nbsp;&nbsp;
              <span style="font-size:11px;font-weight:normal;color:${
                rxData?.status === "Paid" ? "#16a34a" : rxData?.status === "Cancelled" ? "#dc2626" : "#d97706"
              };">${rxData?.status || ""}</span>
            </div>
          </div>

          <div class="header">
            <div class="box" style="max-width:55%;">
              <span class="box-title">Facility Information</span>
              <div style="font-size:16px;font-weight:bold;color:#111;margin-bottom:10px;font-family:serif;">${clinicName}</div>
              <div class="field"><span class="lbl">Address:</span><span class="val">${clinicAddress}</span></div>
              <div class="field"><span class="lbl">Phone:</span><span class="val">${getVal(officeSetting?.office_phone) || "N/A"}</span></div>
              <div class="field"><span class="lbl">Fax:</span><span class="val">${getVal(officeSetting?.office_fax) || "N/A"}</span></div>
              <div class="field"><span class="lbl">Email:</span><span class="val">${getVal(officeSetting?.buisness_email) || "N/A"}</span></div>
              ${billingDataHtml}
            </div>
            <div class="box" style="max-width:42%;text-align:right;">
              <span class="box-title">Customer Information</span>
              <div style="font-size:14px;font-weight:bold;color:#111827;margin-bottom:8px;font-family:sans-serif;text-transform:uppercase;">${pName}</div>
              <div style="font-family:sans-serif;font-size:11px;color:#374151;display:flex;flex-direction:column;gap:3px;">
                <div>${pAddress}</div>
                <div style="font-weight:bold;">${pPhone}</div>
                <div>${pEmail}</div>
                <div style="margin-top:4px;"><span style="font-weight:bold;color:#6b7280;text-transform:uppercase;font-size:8px;margin-right:4px;">Payment:</span>${rxData?.payment_method || "N/A"}</div>
              </div>
              <div style="margin-top:10px;padding-top:8px;border-top:1px solid #e5e7eb;text-align:right;">
                <span class="box-title">Created By</span>
                <div style="font-family:sans-serif;font-size:11px;color:#374151;">
                  <div>${rxData?.created_by || rxData?.added_by || "N/A"}</div>
                  <div>${dateStr} &nbsp; ${timeStr}</div>
                </div>
              </div>
            </div>
          </div>

          <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:20px;border-bottom:2px solid #000;padding-bottom:10px;">
            <div>
              <h2 style="margin:0;text-transform:uppercase;font-family:serif;">Service Invoice</h2>
              <div style="font-size:12px;font-family:monospace;color:#666;margin-top:4px;">ID: ${rxData?.invoice_id || "N/A"}</div>
            </div>
            <div style="text-align:right;font-weight:bold;font-family:sans-serif;font-size:12px;">
              Date: ${dateStr}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width:40px;">#</th>
                <th>Service Name</th>
                <th>QTY</th>
                <th class="right">Unit Price</th>
                <th class="right">Total Price</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>${rxData?.service_name || "N/A"}</td>
                <td>${rxData?.quantity || 1}</td>
                <td class="right">${fmt(rxData?.price)}</td>
                <td class="right">${fmt(totalPrice)}</td>
              </tr>
            </tbody>
          </table>

          <div class="totals">
            <div class="totals-row"><span>Sub Total</span><span>${fmt(subTotal)}</span></div>
            <div class="totals-row"><span>Discount ${rxData?.discount || 0}%</span><span>- ${fmt(rxData?.discounted_amount)}</span></div>
            <div class="totals-row"><span>Tax ${getVal(officeSetting?.local_tax_rate) || 0}%</span><span>${fmt(rxData?.tax)}</span></div>
            <div class="totals-row"><span>Grand Total</span><span>${fmt(rxData?.grand_total_amount)}</span></div>
          </div>
        </body>
      </html>
    `;

    let iframe = document.getElementById("service-invoice-print-iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "service-invoice-print-iframe";
      iframe.style.position = "absolute";
      iframe.style.top = "-9999px";
      iframe.style.left = "-9999px";
      document.body.appendChild(iframe);
    }

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printHtml);
    iframeDoc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 500);
  };

  return (
    <Drawer
      title={rxData?.patient_name}
      placement="right"
      width="900px"
      onClose={onClose}
      open={visible}
      extra={
        <Button
          type="primary"
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          style={{ backgroundColor: "#0ea5e9", borderColor: "#0ea5e9" }}
        >
          Print Now
        </Button>
      }
    >
      <div className="bg-white">
        <div className="p-8 text-black font-serif" style={{ color: "black" }}>

          {/* INVOICE BAR */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "#e2e8f0", padding: "10px 16px", borderRadius: "8px", marginBottom: "24px" }}>
            {officeSetting?.user_office_logo ? (
              <img src={officeSetting.user_office_logo} alt="Logo" style={{ width: "48px", height: "48px", objectFit: "contain" }} />
            ) : (
              <div style={{ width: "48px" }} />
            )}
            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#475569", fontFamily: "sans-serif" }}>
              Invoice ID: {rxData?.invoice_id}
              <span style={{ marginLeft: "12px", fontSize: "12px", fontWeight: "normal", color: statusColor }}>
                {rxData?.status}
              </span>
            </div>
          </div>

          {/* HEADER — Facility + Customer (same layout as LabPrescription) */}
          <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "24px", marginBottom: "16px", gap: "20px", alignItems: "stretch" }}>

            {/* FACILITY BOX */}
            <div style={{ maxWidth: "58%", flex: 1, ...cardStyle }}>
              <Text style={sectionTitleStyle}>Facility Information</Text>
              {officeSetting && (
                <div className="text-left font-sans text-black">
                  <Title level={4} style={{ margin: 0, color: "black", fontFamily: "serif", fontWeight: "bold", letterSpacing: "-0.025em", marginBottom: "12px" }}>
                    {officeSetting.facility_name?.[0]}
                  </Title>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "11px" }}>
                    {isVisible(officeSetting.facility_address) && (
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        <div style={labelStyle}>Address:</div>
                        <div style={valueStyle}>
                          {[getVal(officeSetting.facility_address), getVal(officeSetting.city), getVal(officeSetting.state), getVal(officeSetting.zipcode)].filter(Boolean).join(", ")}
                        </div>
                      </div>
                    )}
                    {isVisible(officeSetting.office_phone) && (
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        <div style={labelStyle}>Phone:</div>
                        <div style={valueStyle}>{getVal(officeSetting.office_phone)}</div>
                      </div>
                    )}
                    {isVisible(officeSetting.office_fax) && (
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        <div style={labelStyle}>Fax:</div>
                        <div style={valueStyle}>{getVal(officeSetting.office_fax)}</div>
                      </div>
                    )}
                    {isVisible(officeSetting.buisness_email) && (
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        <div style={labelStyle}>Email:</div>
                        <div style={valueStyle}>{getVal(officeSetting.buisness_email)}</div>
                      </div>
                    )}
                    {["additional_basic_data_1", "additional_basic_data_2", "additional_basic_data_3"].map((key) => {
                      const field = officeSetting[key];
                      if (!isVisible(field)) return null;
                      return (
                        <div key={key} style={{ display: "flex", alignItems: "flex-start" }}>
                          <div style={labelStyle}>{field?.title || "Data"}:</div>
                          <div style={valueStyle}>{getVal(field)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* CUSTOMER BOX */}
            <div style={{ maxWidth: "40%", flex: 1, display: "flex", flexDirection: "column", ...cardStyle, textAlign: "right" }}>
              <Text style={sectionTitleStyle}>Customer Information</Text>
              <div style={{ fontFamily: "sans-serif", fontSize: "12px", display: "flex", flexDirection: "column", gap: "4px", color: "black" }}>
                <div style={{ fontWeight: "bold", fontSize: "15px", textTransform: "uppercase", lineHeight: 1.25, marginBottom: "6px", color: "#111827" }}>
                  {patientName}
                </div>
                <div style={{ fontSize: "11px", color: "#4b5563", lineHeight: 1.25 }}>{patientAddr || "N/A"}</div>
                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#111827" }}>{patientBasicInfo?.patient_phone || "N/A"}</div>
                <div style={{ fontSize: "11px", color: "#4b5563" }}>{patientBasicInfo?.patient_email || "N/A"}</div>
                <div style={{ fontSize: "11px", marginTop: "2px" }}>
                  <span style={{ fontWeight: "bold", color: "#9ca3af", textTransform: "uppercase", marginRight: "4px", fontSize: "8px" }}>Payment:</span>
                  {rxData?.payment_method || "N/A"}
                </div>
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ paddingTop: "10px", borderTop: "1px solid #e5e7eb", marginTop: "auto" }}>
                <Text style={{ display: "block", color: "#9ca3af", textTransform: "uppercase", fontSize: "8px", fontWeight: "bold", letterSpacing: "0.1em", marginBottom: "4px" }}>
                  Created By
                </Text>
                <div style={{ fontFamily: "sans-serif", fontSize: "11px", color: "#374151" }}>
                  <div style={{ fontWeight: "bold" }}>{rxData?.created_by || rxData?.added_by || "N/A"}</div>
                  <div><FormatDateTime date={issueDate} type="date" /> &nbsp; <FormatDateTime date={issueDate} type="time" /></div>
                </div>
              </div>
            </div>
          </div>

          {/* INVOICE ORDER HEADER */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "24px", borderBottom: "1px solid #d1d5db", paddingBottom: "8px" }}>
            <div>
              <Title level={4} style={{ margin: 0, textTransform: "uppercase" }}>Service Invoice</Title>
              <Text type="secondary" style={{ fontSize: "12px", fontFamily: "monospace" }}>
                ID: {rxData?.invoice_id}
              </Text>
            </div>
            <Text strong>
              Date: <FormatDateTime date={issueDate} type="date" />
            </Text>
          </div>

          {/* SERVICES TABLE */}
          <div style={{ ...cardStyle, marginBottom: "20px" }}>
            <Text style={sectionTitleStyle}>Services</Text>
            <Table
              columns={column}
              size="small"
              dataSource={[rxData]}
              pagination={false}
            />
          </div>

          {/* TOTALS */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ ...cardStyle, minWidth: "300px" }}>
              <Text style={sectionTitleStyle}>Summary</Text>
              <div style={{ display: "flex", flexDirection: "column", fontFamily: "sans-serif", fontSize: "12px" }}>
                {[
                  { label: "Sub Total", value: subTotal },
                  { label: `Discount ${rxData?.discount || 0}%`, value: rxData?.discounted_amount, prefix: "- " },
                  { label: `Tax ${getVal(officeSetting?.local_tax_rate) || 0}%`, value: rxData?.tax },
                ].map(({ label, value, prefix = "" }) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>
                    <span>{label}</span>
                    <span>{prefix}<NumberFormatter value={value} /></span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontWeight: "bold", fontSize: "13px", color: "#111827" }}>
                  <span>Grand Total</span>
                  <span><NumberFormatter value={rxData?.grand_total_amount} /></span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Drawer>
  );
};

export default ServiceInvoicePad;
